import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  DocumentTextIcon,
  EyeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

interface Booking {
  _id: string;
  bookingId: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    country: string;
  };
  package: {
    _id: string;
    name: string;
    packageCode: string;
  };
  selectedResort?: {
    _id: string;
    name: string;
    destination: string;
  };
  payment: {
    amount: number;
    currency: 'USD' | 'AUD';
    status: 'pending' | 'completed' | 'failed';
    transactionId: string;
    paymentMethod: string;
  };
  status: 'pending_payment' | 'voucher_issued' | 'resort_selected' | 'booking_confirmed' | 'completed' | 'cancelled';
  dates?: {
    checkIn: string;
    checkOut: string;
    guests: number;
  };
  voucherExpiry: string;
  communicationHistory: Array<{
    type: 'email' | 'sms' | 'note';
    subject: string;
    message: string;
    timestamp: string;
    sentBy: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

const AdminBookings: React.FC = () => {
  const { token } = useAuth();
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState<boolean>(false);

  const [bookings, setBookings] = useState<Booking[]>([]);

  const mapStatusFromApi = (status: string, paymentStatus?: string): Booking['status'] => {
    switch (status) {
      case 'voucher_purchased':
        return 'voucher_issued';
      case 'resort_selected':
        return 'resort_selected';
      case 'confirmed':
        return 'booking_confirmed';
      case 'completed':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      default:
        return paymentStatus === 'pending' ? 'pending_payment' : 'voucher_issued';
    }
  };

  const mapStatusToApi = (status: string): string => {
    switch (status) {
      case 'voucher_issued':
        return 'voucher_purchased';
      case 'booking_confirmed':
        return 'confirmed';
      default:
        return status; // resort_selected, completed, cancelled
    }
  };

  const mapApiBooking = (b: any): Booking => ({
    _id: b._id,
    bookingId: b.bookingCode,
    customer: {
      firstName: b.customer?.firstName || '',
      lastName: b.customer?.lastName || '',
      email: b.customer?.email || '',
      phone: `${b.customer?.phone?.countryCode || ''}${b.customer?.phone?.number || ''}`,
      country: ''
    },
    package: {
      _id: b.package?._id || '',
      name: b.package?.name || '',
      packageCode: b.package?.packageCode || ''
    },
    selectedResort: b.selectedResort ? {
      _id: b.selectedResort._id,
      name: b.selectedResort.name,
      destination: b.selectedResort.resortCode || ''
    } : undefined,
    payment: {
      amount: b.amount,
      currency: b.currency,
      status: b.paymentDetails?.paymentStatus || 'completed',
      transactionId: b.paymentDetails?.transactionId || '',
      paymentMethod: b.paymentDetails?.paymentMethod || 'Flywire'
    },
    status: mapStatusFromApi(b.status, b.paymentDetails?.paymentStatus),
    dates: b.checkInDate && b.checkOutDate ? {
      checkIn: b.checkInDate,
      checkOut: b.checkOutDate,
      guests: 2
    } : undefined,
    voucherExpiry: b.voucherExpiryDate,
    communicationHistory: [],
    createdAt: b.createdAt,
    updatedAt: b.updatedAt
  });

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        const params: any = {};
        if (searchQuery) params.search = searchQuery;
        if (filterStatus !== 'all') params.status = mapStatusToApi(filterStatus);
        const res = await axios.get('/api/bookings', { params });
        const items = res.data?.bookings || [];
        setBookings(items.map(mapApiBooking));
      } catch (e) {
        console.error('Failed to fetch bookings', e);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [filterStatus, searchQuery, token]);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending_payment', label: 'Pending Payment' },
    { value: 'voucher_issued', label: 'Voucher Issued' },
    { value: 'resort_selected', label: 'Resort Selected' },
    { value: 'booking_confirmed', label: 'Booking Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const filteredBookings = bookings.filter(booking => {
    const statusMatch = filterStatus === 'all' || booking.status === filterStatus;
    const searchMatch = searchQuery === '' || 
      booking.bookingId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `${booking.customer.firstName} ${booking.customer.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.customer.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    return statusMatch && searchMatch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'booking_confirmed':
        return 'bg-orange-100 text-orange-800';
      case 'resort_selected':
        return 'bg-amber-100 text-amber-800';
      case 'voucher_issued':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending_payment':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return CheckCircleIcon;
      case 'booking_confirmed':
        return CalendarIcon;
      case 'resort_selected':
        return BuildingOfficeIcon;
      case 'voucher_issued':
        return DocumentTextIcon;
      case 'pending_payment':
        return ClockIcon;
      case 'cancelled':
        return XCircleIcon;
      default:
        return DocumentTextIcon;
    }
  };

  const handleViewDetails = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowDetails(true);
  };

  const handleStatusUpdate = async (bookingId: string, newStatus: string) => {
    try {
      const apiStatus = mapStatusToApi(newStatus);
      await axios.patch(`/api/bookings/${bookingId}/status`, { status: apiStatus });
      setBookings(prev => prev.map(booking => 
        booking._id === bookingId ? { ...booking, status: newStatus as any, updatedAt: new Date().toISOString() } : booking
      ));
    } catch (e) {
      console.error('Failed to update booking status', e);
    }
  };

  return (
    <>
      <Helmet>
        <title>Bookings - Bali Getaway Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
            <p className="text-gray-600">Manage customer bookings and vouchers</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
                <p className="text-sm text-gray-500">Total Bookings</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {bookings.filter(b => b.status === 'booking_confirmed' || b.status === 'completed').length}
                </p>
                <p className="text-sm text-gray-500">Confirmed Bookings</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {bookings.filter(b => b.status === 'pending_payment').length}
                </p>
                <p className="text-sm text-gray-500">Pending Payment</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  ${bookings.filter(b => b.payment.status === 'completed')
                    .reduce((sum, b) => sum + b.payment.amount, 0).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">Total Revenue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by booking ID, customer name, or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status Filter
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="form-input"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Bookings Table */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              All Bookings ({filteredBookings.length})
            </h2>
          </div>
          
          {filteredBookings.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {bookings.length === 0 
                  ? 'No bookings have been made yet.' 
                  : 'Try adjusting your search or filters.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Booking
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Package
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBookings.map((booking) => {
                    const StatusIcon = getStatusIcon(booking.status);
                    return (
                      <tr key={booking._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {booking.bookingId}
                            </div>
                            <div className="text-sm text-gray-500">
                              Expires: {new Date(booking.voucherExpiry).toLocaleDateString()}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <UserIcon className="h-5 w-5 text-gray-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {booking.customer.firstName} {booking.customer.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {booking.customer.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{booking.package.name}</div>
                          <div className="text-sm text-gray-500">{booking.package.packageCode}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {booking.payment.currency} ${booking.payment.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <StatusIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                              {booking.status.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(booking.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(booking)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            className="text-green-600 hover:text-green-900"
                            title="Send Communication"
                          >
                            <PaperAirplaneIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Booking Details Modal */}
      {showDetails && selectedBooking && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Booking Details - {selectedBooking.bookingId}
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Customer Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Name:</strong> {selectedBooking.customer.firstName} {selectedBooking.customer.lastName}</p>
                    <p><strong>Email:</strong> {selectedBooking.customer.email}</p>
                    <p><strong>Phone:</strong> {selectedBooking.customer.phone}</p>
                    <p><strong>Country:</strong> {selectedBooking.customer.country}</p>
                  </div>
                </div>

                {/* Package Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Package Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Package:</strong> {selectedBooking.package.name}</p>
                    <p><strong>Code:</strong> {selectedBooking.package.packageCode}</p>
                    <p><strong>Voucher Expires:</strong> {new Date(selectedBooking.voucherExpiry).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Payment Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Amount:</strong> {selectedBooking.payment.currency} ${selectedBooking.payment.amount.toLocaleString()}</p>
                    <p><strong>Status:</strong> 
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        selectedBooking.payment.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : selectedBooking.payment.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedBooking.payment.status}
                      </span>
                    </p>
                    <p><strong>Transaction ID:</strong> {selectedBooking.payment.transactionId}</p>
                    <p><strong>Method:</strong> {selectedBooking.payment.paymentMethod}</p>
                  </div>
                </div>

                {/* Booking Status */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Booking Status</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Current Status:</strong> 
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedBooking.status)}`}>
                        {selectedBooking.status.replace('_', ' ')}
                      </span>
                    </p>
                    <p><strong>Created:</strong> {new Date(selectedBooking.createdAt).toLocaleString()}</p>
                    <p><strong>Last Updated:</strong> {new Date(selectedBooking.updatedAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Resort Selection */}
              {selectedBooking.selectedResort && (
                <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Resort Selection</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Resort:</strong> {selectedBooking.selectedResort.name}</p>
                    <p><strong>Destination:</strong> {selectedBooking.selectedResort.destination}</p>
                    {selectedBooking.dates && (
                      <>
                        <p><strong>Check-in:</strong> {new Date(selectedBooking.dates.checkIn).toLocaleDateString()}</p>
                        <p><strong>Check-out:</strong> {new Date(selectedBooking.dates.checkOut).toLocaleDateString()}</p>
                        <p><strong>Guests:</strong> {selectedBooking.dates.guests}</p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Communication History */}
              <div className="mt-6">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Communication History</h4>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {selectedBooking.communicationHistory.map((comm, index) => (
                    <div key={index} className="border-l-4 border-blue-200 pl-4 py-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{comm.subject}</p>
                          <p className="text-sm text-gray-600 mt-1">{comm.message}</p>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <p>{new Date(comm.timestamp).toLocaleDateString()}</p>
                          <p>by {comm.sentBy}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <select 
                  className="form-input mr-4"
                  value={selectedBooking.status}
                  onChange={(e) => handleStatusUpdate(selectedBooking._id, e.target.value)}
                >
                  {statusOptions.filter(s => s.value !== 'all').map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button className="btn-secondary">
                  Send Email
                </button>
                <button 
                  onClick={() => setShowDetails(false)}
                  className="btn-primary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminBookings;
